import React, { useContext, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { UserContext } from '../context/User';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Container,
  Dropdown,
  Icon,
  Menu,
  Segment,
} from 'semantic-ui-react';
import {
  API,
  getLogo,
  getSystemName,
  isAdmin,
  isMobile,
  showSuccess,
} from '../helpers';
import '../index.css';

// Header Buttons
let headerButtons = [
  {
    name: 'header.channel',
    to: '/channel',
    icon: 'sitemap',
    admin: true,
  },
  {
    name: 'header.token',
    to: '/token',
    icon: 'key',
  },
  {
    name: 'header.redemption',
    to: '/redemption',
    icon: 'dollar sign',
    admin: true,
  },
  {
    name: 'header.topup',
    to: '/topup',
    icon: 'cart',
  },
  {
    name: 'header.user',
    to: '/user',
    icon: 'user',
    admin: true,
  },
  {
    name: 'header.payment_orders',
    to: '/payment/orders',
    icon: 'list alternate',
  },
  {
    name: 'header.dashboard',
    to: '/dashboard',
    icon: 'chart bar',
  },
  {
    name: 'header.log',
    to: '/log',
    icon: 'book',
  },
  {
    name: 'header.setting',
    to: '/setting',
    icon: 'setting',
    admin: true,
  },
];

if (localStorage.getItem('chat_link')) {
  headerButtons.splice(1, 0, {
    name: 'header.chat',
    to: '/chat',
    icon: 'comments',
  });
}

const Header = () => {
  const { t, i18n } = useTranslation();
  const [userState, userDispatch] = useContext(UserContext);
  let navigate = useNavigate();
  const location = useLocation();

  const [showSidebar, setShowSidebar] = useState(false);
  const systemName = getSystemName();
  const logo = getLogo();

  async function logout() {
    setShowSidebar(false);
    await API.get('/api/user/logout');
    showSuccess('注销成功!');
    userDispatch({ type: 'logout' });
    localStorage.removeItem('user');
    navigate('/login');
  }

  const toggleSidebar = () => {
    setShowSidebar(!showSidebar);
  };

  const renderButtons = (isMobile) => {
    return headerButtons.map((button) => {
      if (button.admin && !isAdmin()) return <></>;
      const isActive =
        button.to === '/'
          ? location.pathname === '/'
          : location.pathname.startsWith(button.to);
      if (isMobile) {
        return (
          <Menu.Item
            key={button.name}
            active={isActive}
            onClick={() => {
              navigate(button.to);
              setShowSidebar(false);
            }}
            className='mobile-nav-item'
          >
            <Icon name={button.icon} />
            {t(button.name)}
          </Menu.Item>
        );
      }
      return (
        <Menu.Item
          key={button.name}
          as={Link}
          to={button.to}
          active={isActive}
          className='admin-nav-item'
        >
          <Icon name={button.icon} />
          {t(button.name)}
        </Menu.Item>
      );
    });
  };

  // Add language switcher dropdown
  const languageOptions = [
    { key: 'zh', text: '中文', value: 'zh' },
    { key: 'en', text: 'English', value: 'en' },
  ];

  const changeLanguage = (language) => {
    i18n.changeLanguage(language);
  };

  if (isMobile()) {
    return (
      <>
        <Menu borderless size='large' className='mobile-admin-header'>
          <Container className='mobile-admin-header-inner'>
            <Menu.Item as={Link} to='/' className='admin-brand mobile-brand'>
              <img src={logo} alt='logo' />
              <div>
                <b>{systemName}</b>
                <span>引领信息智能化科技前沿</span>
              </div>
            </Menu.Item>
            <Menu.Menu position='right'>
              <Menu.Item onClick={toggleSidebar}>
                <Icon name={showSidebar ? 'close' : 'sidebar'} />
              </Menu.Item>
            </Menu.Menu>
          </Container>
        </Menu>
        {showSidebar ? (
          <Segment className='mobile-admin-panel'>
            <Menu secondary vertical className='mobile-admin-menu'>
              {renderButtons(true)}
              <Menu.Item>
                <Dropdown
                  selection
                  trigger={
                    <Icon
                      name='language'
                      style={{ margin: 0, fontSize: '18px' }}
                    />
                  }
                  options={languageOptions}
                  value={i18n.language}
                  onChange={(_, { value }) => changeLanguage(value)}
                />
              </Menu.Item>
              <Menu.Item>
                {userState.user ? (
                  <Button onClick={logout} style={{ color: '#666666' }}>
                    {t('header.logout')}
                  </Button>
                ) : (
                  <>
                    <Button
                      onClick={() => {
                        setShowSidebar(false);
                        navigate('/login');
                      }}
                    >
                      {t('header.login')}
                    </Button>
                    <Button
                      onClick={() => {
                        setShowSidebar(false);
                        navigate('/register');
                      }}
                    >
                      {t('header.register')}
                    </Button>
                  </>
                )}
              </Menu.Item>
            </Menu>
          </Segment>
        ) : (
          <></>
        )}
      </>
    );
  }

  return (
    <>
      <Menu borderless vertical fixed='left' className='admin-sidebar'>
        <Menu.Item as={Link} to='/' className='admin-brand'>
          <img src={logo} alt='logo' />
          <div>
            <b>{systemName}</b>
            <span>引领信息智能化科技前沿</span>
          </div>
        </Menu.Item>
        <div className='admin-nav-section'>
          <div className='admin-nav-title'>控制台</div>
          {renderButtons(false)}
        </div>
      </Menu>
      <Menu borderless fixed='top' className='admin-topbar'>
        <Menu.Menu position='right'>
          <Dropdown
            item
            trigger={
              <span className='topbar-icon-button'>
                <Icon name='language' />
              </span>
            }
            options={languageOptions}
            value={i18n.language}
            onChange={(_, { value }) => changeLanguage(value)}
          />
          {userState.user ? (
            <Dropdown
              pointing
              className='link item admin-user-dropdown'
              trigger={
                <span className='admin-user-chip'>
                  <span className='admin-user-avatar'>
                    {userState.user.username?.[0]?.toUpperCase() || 'U'}
                  </span>
                  <span>{userState.user.username}</span>
                </span>
              }
            >
              <Dropdown.Menu>
                <Dropdown.Item onClick={logout}>
                  {t('header.logout')}
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          ) : (
            <Menu.Item as={Link} to='/login' className='admin-login-link'>
              <Icon name='sign in' />
              {t('header.login')}
            </Menu.Item>
          )}
        </Menu.Menu>
      </Menu>
    </>
  );
};

export default Header;
